import React, { useEffect, useMemo, useRef, useState } from "react";
import FieldContainer from "../../components/forms/fieldContainer/FieldContainer";
import { Button, Input, Select, Empty } from "antd";
import { DeleteOutlined, LeftOutlined, PlusOutlined } from "@ant-design/icons";
import Loader from "../../components/loader/Loader";
import useUserContext from "../../hooks/useUserContext";
import useToast from "../../hooks/useToast";
import { ToastSkin } from "../../context/ToastContext";
import {
    CreateKnowledgeBaseDocument,
    DeleteKnowledgeBaseDocument,
    GetKnowledgeBaseDocument,
    KnowledgeBaseDocumentDto,
    KnowledgeBaseSourceType,
    KnowledgeBaseStatus,
    ListKnowledgeBaseDocuments,
    UpdateKnowledgeBaseDocument,
} from "../../api/v2/knowledgeBase";
import "./knowledge-base-page.css";

const { TextArea } = Input;

const DEFAULT_FORM = {
    sourceType: "custom" as KnowledgeBaseSourceType,
    status: "active" as KnowledgeBaseStatus,
    title: "",
    language: "en",
    sourceKey: "",
    contentText: "",
};

const SOURCE_OPTIONS = [
    { label: "FAQ", value: "faq" },
    { label: "Policy", value: "policy" },
    { label: "Brand", value: "brand" },
    { label: "Custom", value: "custom" },
];

const STATUS_OPTIONS = [
    { label: "Draft", value: "draft" },
    { label: "Active", value: "active" },
    { label: "Archived", value: "archived" },
];

const SUGGESTED_TOPICS: Array<{ title: string; sourceType: KnowledgeBaseSourceType }> = [
    { title: "Shipping Times", sourceType: "policy" },
    { title: "Shipping Costs", sourceType: "policy" },
    { title: "Delivery Methods", sourceType: "policy" },
    { title: "Shipping Delays", sourceType: "policy" },
    { title: "Address Changes", sourceType: "faq" },
    { title: "International Shipping", sourceType: "policy" },
    { title: "Return Policy", sourceType: "policy" },
    { title: "Exchange Policy", sourceType: "policy" },
    { title: "Return Conditions", sourceType: "policy" },
    { title: "Return Process", sourceType: "faq" },
    { title: "Refund Timeline", sourceType: "policy" },
    { title: "Payment Methods", sourceType: "faq" },
    { title: "Payment Security", sourceType: "policy" },
    { title: "Installments / Payment Plans", sourceType: "faq" },
    { title: "Payment Issues", sourceType: "faq" },
    { title: "Create an Account", sourceType: "faq" },
    { title: "Forgot Password", sourceType: "faq" },
    { title: "Update Personal Details", sourceType: "faq" },
    { title: "Manage Orders", sourceType: "faq" },
    { title: "How to Place an Order", sourceType: "faq" },
    { title: "Order Status", sourceType: "faq" },
    { title: "Modify or Cancel an Order", sourceType: "faq" },
    { title: "Missing Orders", sourceType: "faq" },
    { title: "Damaged or Incorrect Orders", sourceType: "faq" },
    { title: "How to Use a Coupon", sourceType: "faq" },
    { title: "Coupon Limitations", sourceType: "policy" },
    { title: "Invalid Coupon Issues", sourceType: "faq" },
    { title: "Coupon Expiration", sourceType: "policy" },
    { title: "About Us", sourceType: "brand" },
    { title: "Contact Us", sourceType: "brand" },
    { title: "Business Hours", sourceType: "brand" },
    { title: "Store Location", sourceType: "brand" },
    { title: "Shipping Time", sourceType: "faq" },
    { title: "Free Shipping", sourceType: "faq" },
    { title: "Returns", sourceType: "faq" },
    { title: "Order Tracking", sourceType: "faq" },
    { title: "Pickup Options", sourceType: "faq" },
    { title: "Payment Options", sourceType: "faq" },
    { title: "Cancellation Policy", sourceType: "policy" },
    { title: "Product Warranty", sourceType: "policy" },
    { title: "Failed Delivery Attempts", sourceType: "faq" },
    { title: "Re-delivery Process", sourceType: "faq" },
    { title: "Gift Wrapping", sourceType: "faq" },
    { title: "Out of Stock After Purchase", sourceType: "faq" },
    { title: "Order Mistakes", sourceType: "faq" },
    { title: "Quick Contact (WhatsApp Support)", sourceType: "brand" },
];

export type KnowledgeBaseDraftSyncState = "clean" | "created" | "updated" | "deleted";

export interface KnowledgeBaseDraftDocument {
    id: number;
    shopId?: number;
    sourceType: KnowledgeBaseSourceType;
    status: KnowledgeBaseStatus;
    title: string;
    language: string;
    sourceKey?: string | null;
    contentText: string;
    metadataJson?: any | null;
    createdAt?: string;
    updatedAt?: string;
    chunksCount?: number;
    syncState: KnowledgeBaseDraftSyncState;
    hydrated?: boolean;
}

interface EmbeddedKnowledgeBaseState {
    documents: KnowledgeBaseDraftDocument[];
    selectedDocumentId: number | null;
}

interface KnowledgeBaseEditorProps {
    embedded?: boolean;
    panelMode?: boolean;
    embeddedState?: EmbeddedKnowledgeBaseState | null;
    onEmbeddedStateChange?: (state: EmbeddedKnowledgeBaseState) => void;
    reloadToken?: number;
    createToken?: number;
}

const toDraftDocument = (document: KnowledgeBaseDocumentDto): KnowledgeBaseDraftDocument => ({
    ...document,
    sourceKey: document.sourceKey || "",
    chunksCount: Number(document.chunksCount || 0),
    syncState: "clean",
    hydrated: !!document.contentText,
});

const serializeEmbeddedState = (documents: KnowledgeBaseDraftDocument[], selectedDocumentId: number | null): EmbeddedKnowledgeBaseState => ({
    documents,
    selectedDocumentId,
});

const buildSourceKey = (title: string) => title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

export default function KnowledgeBaseEditor({
    embedded = false,
    panelMode = false,
    embeddedState = null,
    onEmbeddedStateChange,
    reloadToken = 0,
    createToken = 0,
}: KnowledgeBaseEditorProps) {
    const { selectedDomain } = useUserContext();
    const { showToast, closeToast } = useToast();
    const [documents, setDocuments] = useState<KnowledgeBaseDraftDocument[]>([]);
    const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [chunkCount, setChunkCount] = useState(0);
    const tempIdRef = useRef(-1);
    const [embeddedStep, setEmbeddedStep] = useState<"list" | "editor">("list");
    const isStepMode = embedded || panelMode;
    const lastCreateTokenRef = useRef(createToken);
    const [isInitialDataReady, setIsInitialDataReady] = useState(false);

    const visibleDocuments = useMemo(
        () => documents.filter((document) => document.syncState !== "deleted"),
        [documents]
    );

    const selectedDocument = useMemo(
        () => documents.find((document) => document.id === selectedDocumentId) || null,
        [documents, selectedDocumentId]
    );

    const form = useMemo(() => {
        if (!selectedDocument) return DEFAULT_FORM;
        return {
            sourceType: selectedDocument.sourceType,
            status: selectedDocument.status,
            title: selectedDocument.title,
            language: selectedDocument.language,
            sourceKey: selectedDocument.sourceKey || "",
            contentText: selectedDocument.contentText,
        };
    }, [selectedDocument]);

    const pushEmbeddedState = (nextDocuments: KnowledgeBaseDraftDocument[], nextSelectedDocumentId: number | null) => {
        if (!embedded || !onEmbeddedStateChange) return;
        onEmbeddedStateChange(serializeEmbeddedState(nextDocuments, nextSelectedDocumentId));
    };

    const commitDocuments = (
        updater: KnowledgeBaseDraftDocument[] | ((prev: KnowledgeBaseDraftDocument[]) => KnowledgeBaseDraftDocument[]),
        nextSelectedDocumentId?: number | null
    ) => {
        setDocuments((prev) => {
            const resolved = typeof updater === "function" ? updater(prev) : updater;
            const selectedId = nextSelectedDocumentId === undefined ? selectedDocumentId : nextSelectedDocumentId;
            pushEmbeddedState(resolved, selectedId ?? null);
            return resolved;
        });
        if (nextSelectedDocumentId !== undefined) {
            setSelectedDocumentId(nextSelectedDocumentId);
        }
    };

    const hydrateDocument = async (documentId: number) => {
        if (!selectedDomain) return;
        setIsLoading(true);
        const response = await GetKnowledgeBaseDocument(selectedDomain, documentId);
        setIsLoading(false);
        if (!response.ok || !response.data?.document) {
            showToast({ skin: ToastSkin.danger, text: "Failed to load the selected item." });
            return;
        }
        const hydratedDocument = toDraftDocument(response.data.document);
        hydratedDocument.chunksCount = response.data.chunks?.length || 0;
        commitDocuments((prev) => prev.map((document) => (
            document.id === documentId
                ? {
                    ...document,
                    ...hydratedDocument,
                    syncState: document.syncState === "created" ? "created" : document.syncState,
                    hydrated: true,
                }
                : document
        )));
        if (selectedDocumentId === documentId) {
            setChunkCount(hydratedDocument.chunksCount || 0);
        }
    };

    const loadDocuments = async (preferredId?: number | null) => {
        if (!selectedDomain) return;
        setIsLoading(true);
        const response = await ListKnowledgeBaseDocuments(selectedDomain);
        setIsLoading(false);
        if (!response.ok || !response.data?.documents) {
            showToast({ skin: ToastSkin.danger, text: "Failed to load knowledge base." });
            return;
        }

        const nextDocuments = response.data.documents.map(toDraftDocument);
        const nextSelectedId = preferredId ?? selectedDocumentId;
        let targetId: number | null = null;

        if (nextDocuments.length) {
            targetId = nextSelectedId && nextDocuments.some((document) => document.id === nextSelectedId)
                ? nextSelectedId
                : nextDocuments[0].id;
        }

        setDocuments(nextDocuments);
        setSelectedDocumentId(targetId);
        pushEmbeddedState(nextDocuments, targetId);

        if (!targetId) {
            setChunkCount(0);
        }
        setIsInitialDataReady(true);
    };

    useEffect(() => {
        if (embedded && embeddedState) {
            setDocuments(embeddedState.documents);
            setSelectedDocumentId(embeddedState.selectedDocumentId);
        }
    }, [embedded, embeddedState]);

    useEffect(() => {
        if (!embedded) return;
        if (selectedDocumentId) {
            setEmbeddedStep("editor");
        } else {
            setEmbeddedStep("list");
        }
    }, [embedded, selectedDocumentId]);

    useEffect(() => {
        if (!panelMode) return;
        setEmbeddedStep("list");
    }, [panelMode]);

    useEffect(() => {
        if (embedded) {
            if (embeddedState?.documents?.length && reloadToken === 0) return;
            loadDocuments(null);
            return;
        }
        loadDocuments(null);
    }, [selectedDomain, reloadToken]);

    useEffect(() => {
        if (!selectedDocument) {
            setChunkCount(0);
            return;
        }
        setChunkCount(Number(selectedDocument.chunksCount || 0));
        if (selectedDocumentId && !selectedDocument.hydrated && selectedDocumentId > 0) {
            hydrateDocument(selectedDocumentId);
        }
    }, [selectedDocumentId, selectedDocument]);

    useEffect(() => {
        if (!panelMode) return;
        if (createToken === lastCreateTokenRef.current) return;
        lastCreateTokenRef.current = createToken;
        onCreateNew();
    }, [createToken, panelMode]);

    const updateSelectedDocument = (patch: Partial<KnowledgeBaseDraftDocument>) => {
        if (!selectedDocumentId) return;
        commitDocuments((prev) => prev.map((document) => {
            if (document.id !== selectedDocumentId) return document;
            const nextSyncState = document.syncState === "clean" ? "updated" : document.syncState;
            return {
                ...document,
                ...patch,
                syncState: nextSyncState,
                hydrated: true,
            };
        }));
    };

    const onCreateNew = () => {
        const nextId = tempIdRef.current;
        tempIdRef.current -= 1;
        const nextDocument: KnowledgeBaseDraftDocument = {
            id: nextId,
            ...DEFAULT_FORM,
            sourceKey: "",
            chunksCount: 0,
            syncState: "created",
            hydrated: true,
        };
        const nextDocuments = [nextDocument, ...documents];
        setChunkCount(0);
        setDocuments(nextDocuments);
        setSelectedDocumentId(nextId);
        if (isStepMode) setEmbeddedStep("editor");
        pushEmbeddedState(nextDocuments, nextId);
    };

    const onCreateSuggested = (suggestion: { title: string; sourceType: KnowledgeBaseSourceType }) => {
        const existing = visibleDocuments.find((document) => document.title.trim().toLowerCase() === suggestion.title.trim().toLowerCase());
        if (existing) {
            setSelectedDocumentId(existing.id);
            setEmbeddedStep("editor");
            return;
        }

        const nextId = tempIdRef.current;
        tempIdRef.current -= 1;
        const nextDocument: KnowledgeBaseDraftDocument = {
            id: nextId,
            ...DEFAULT_FORM,
            title: suggestion.title,
            sourceType: suggestion.sourceType,
            sourceKey: buildSourceKey(suggestion.title),
            chunksCount: 0,
            syncState: "created",
            hydrated: true,
        };
        const nextDocuments = [nextDocument, ...documents];
        setChunkCount(0);
        setDocuments(nextDocuments);
        setSelectedDocumentId(nextId);
        if (isStepMode) setEmbeddedStep("editor");
        pushEmbeddedState(nextDocuments, nextId);
    };

    const onSave = async () => {
        if (!selectedDomain) return;
        if (!form.title.trim() || !form.contentText.trim()) {
            showToast({ skin: ToastSkin.neutral, text: "Title and content are required." });
            return;
        }
        setIsSaving(true);
        const payload = {
            sourceType: form.sourceType,
            status: form.status,
            title: form.title.trim(),
            language: form.language.trim() || "en",
            sourceKey: form.sourceKey.trim() || null,
            contentText: form.contentText.trim(),
            metadataJson: null,
        };
        const response = selectedDocumentId && selectedDocumentId > 0
            ? await UpdateKnowledgeBaseDocument(selectedDomain, selectedDocumentId, payload)
            : await CreateKnowledgeBaseDocument(selectedDomain, payload);
        setIsSaving(false);
        if (!response.ok || !response.data?.document) {
            showToast({ skin: ToastSkin.danger, text: "Failed to save knowledge item." });
            return;
        }
        await loadDocuments(response.data.document.id);
        if (isStepMode) {
            setEmbeddedStep("list");
        }
        showToast({ skin: ToastSkin.approval, text: "Knowledge item saved." });
    };

    const removeDocumentFromState = (documentId: number) => {
        const target = documents.find((document) => document.id === documentId);
        if (!target) return;

        let nextDocuments: KnowledgeBaseDraftDocument[];
        if (target.syncState === "created") {
            nextDocuments = documents.filter((document) => document.id !== documentId);
        } else {
            nextDocuments = documents.map((document) => (
                document.id === documentId
                    ? { ...document, syncState: "deleted" }
                    : document
            ));
        }

        const nextVisibleDocuments = nextDocuments.filter((document) => document.syncState !== "deleted");
        const nextSelectedId = nextVisibleDocuments[0]?.id ?? null;
        setChunkCount(0);
        setDocuments(nextDocuments);
        setSelectedDocumentId(nextSelectedId);
        pushEmbeddedState(nextDocuments, nextSelectedId);
    };

    const onDeleteEmbedded = () => {
        if (!selectedDocumentId) return;
        showToast({
            skin: ToastSkin.danger,
            text: "Remove this knowledge item from the draft changes? It will not be published until you update the widget settings.",
            buttons: [
                {
                    text: "Delete",
                    onClick: () => {
                        removeDocumentFromState(selectedDocumentId);
                        closeToast();
                    },
                },
                {
                    text: "Cancel",
                    onClick: closeToast,
                },
            ],
        });
    };

    const onDeletePersisted = async () => {
        if (!selectedDomain || !selectedDocumentId) return;
        setIsDeleting(true);
        const response = await DeleteKnowledgeBaseDocument(selectedDomain, selectedDocumentId);
        setIsDeleting(false);
        if (!response.ok) {
            showToast({ skin: ToastSkin.danger, text: "Failed to delete knowledge item." });
            return;
        }
        await loadDocuments(null);
        showToast({ skin: ToastSkin.approval, text: "Knowledge item deleted." });
    };

    const editorActions = embedded ? (
        <div className="knowledge-base-embedded-editor-topbar">
            <button
                type="button"
                className="knowledge-base-back-btn"
                onClick={() => setEmbeddedStep("list")}
            >
                <LeftOutlined />
                <span>Back to items</span>
            </button>
            <Button
                className="knowledge-base-delete-btn"
                type="text"
                size="large"
                icon={<DeleteOutlined />}
                onClick={onDeleteEmbedded}
                disabled={!selectedDocumentId}
                aria-label="Delete knowledge item"
            />
        </div>
    ) : panelMode ? (
        <div className="knowledge-base-embedded-editor-topbar">
            <button
                type="button"
                className="knowledge-base-back-btn"
                onClick={() => setEmbeddedStep("list")}
            >
                <LeftOutlined />
                <span>Back to items</span>
            </button>
            <div className="knowledge-base-panel-editor-actions">
                <Button type="primary" size="large" onClick={onSave} loading={isSaving}>
                    Save Changes
                </Button>
                <Button
                    className="knowledge-base-delete-btn"
                    type="text"
                    size="large"
                    icon={<DeleteOutlined />}
                    onClick={onDeletePersisted}
                    loading={isDeleting}
                    disabled={!selectedDocumentId}
                    aria-label="Delete knowledge item"
                />
            </div>
        </div>
    ) : (
        <div className="knowledge-base-inline-actions">
            <Button type="primary" size="large" onClick={onSave} loading={isSaving}>
                {selectedDocument ? "Save Changes" : "Create Item"}
            </Button>
            {selectedDocumentId ? (
                <Button size="large" danger onClick={onDeletePersisted} loading={isDeleting}>
                    Delete
                </Button>
            ) : null}
        </div>
    );

    const isPanelListReady = !panelMode || (isInitialDataReady && !isLoading);
    const isPanelEditorReady = !panelMode || (!!selectedDocument && (!!selectedDocument.hydrated || Number(selectedDocument.id) < 0) && !isLoading);

    if (isStepMode) {
        return (
            <div className="knowledge-base-embedded-flow">
                {embeddedStep === "list" ? (
                    !isPanelListReady ? (
                        <div className="knowledge-base-panel-loader">
                            <Loader size={72} />
                        </div>
                    ) : (
                    <>
                        {visibleDocuments.length ? (
                            <div className="knowledge-base-card-grid">
                                {visibleDocuments.map((document) => (
                                    <button
                                        key={document.id}
                                        type="button"
                                        className={`knowledge-base-card-item ${selectedDocumentId === document.id ? "selected" : ""}`}
                                        onClick={() => {
                                            setSelectedDocumentId(document.id);
                                            setEmbeddedStep("editor");
                                        }}
                                    >
                                        <div className="knowledge-base-card-item-row">
                                            <span className={`knowledge-base-card-status-dot ${document.status === "active" ? "active" : "inactive"}`} />
                                            <div className="knowledge-base-card-item-title">{document.title || "Untitled item"}</div>
                                        </div>
                                        <div className="knowledge-base-card-item-type">{document.sourceType}</div>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="knowledge-base-empty-state">
                                <Empty description="No knowledge items yet" />
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<PlusOutlined />}
                                    onClick={onCreateNew}
                                >
                                    Create knowledge item
                                </Button>
                            </div>
                        )}
                        <div className="knowledge-base-suggestions-section">
                            <div className="knowledge-base-suggestions-title">Suggested topics</div>
                            <div className="knowledge-base-suggestions-subtitle">Common support topics you may want to cover in your knowledge base.</div>
                            <div className="knowledge-base-suggestions-grid">
                                {SUGGESTED_TOPICS.map((suggestion) => (
                                    <button
                                        key={suggestion.title}
                                        type="button"
                                        className="knowledge-base-suggestion-chip"
                                        onClick={() => onCreateSuggested(suggestion)}
                                    >
                                        {suggestion.title}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </>
                    )
                ) : (
                    !isPanelEditorReady ? (
                        <div className="knowledge-base-panel-loader">
                            <Loader size={72} />
                        </div>
                    ) : (
                        <div className="knowledge-base-editor">
                            {editorActions}
                            <FieldContainer>
                                <div className="knowledge-base-grid">
                                    <div>
                                        <label>Title</label>
                                        <Input
                                            size="large"
                                            value={form.title}
                                            onChange={(e) => updateSelectedDocument({ title: e.target.value })}
                                            placeholder="Shipping policy"
                                        />
                                    </div>
                                    <div>
                                        <label>Source Type</label>
                                        <Select
                                            size="large"
                                            value={form.sourceType}
                                            options={SOURCE_OPTIONS}
                                            getPopupContainer={(trigger) => trigger.parentElement!}
                                            onChange={(value) => updateSelectedDocument({ sourceType: value })}
                                        />
                                    </div>
                                    <div>
                                        <label>Status</label>
                                        <Select
                                            size="large"
                                            value={form.status}
                                            options={STATUS_OPTIONS}
                                            getPopupContainer={(trigger) => trigger.parentElement!}
                                            onChange={(value) => updateSelectedDocument({ status: value })}
                                        />
                                    </div>
                                    <div className="knowledge-base-grid-span-2">
                                        <label>Source Key</label>
                                        <Input
                                            size="large"
                                            value={form.sourceKey}
                                            onChange={(e) => updateSelectedDocument({ sourceKey: e.target.value })}
                                            placeholder="shipping_policy"
                                        />
                                    </div>
                                </div>
                                <div className="knowledge-base-content-field">
                                    <label>Content</label>
                                    <TextArea
                                        value={form.contentText}
                                        onChange={(e) => updateSelectedDocument({ contentText: e.target.value })}
                                        autoSize={{ minRows: 12, maxRows: 18 }}
                                        placeholder="Paste the exact support content the bot is allowed to rely on."
                                    />
                                    <div className="knowledge-base-field-note">Current chunk count: {chunkCount || 0}</div>
                                </div>
                            </FieldContainer>
                        </div>
                    )
                )}
            </div>
        );
    }

    return (
        <div className={`knowledge-base-layout ${embedded ? "embedded" : ""}`}>
            <div className="knowledge-base-sidebar">
                <div className="knowledge-base-sidebar-header">
                    <div>
                        <div className="knowledge-base-sidebar-title">Items</div>
                        <div className="knowledge-base-sidebar-subtitle">Static sources for retrieval.</div>
                    </div>
                    <Button size="large" onClick={onCreateNew}>New</Button>
                </div>
                <div className="knowledge-base-list">
                    {visibleDocuments.length ? visibleDocuments.map((document) => (
                        <button
                            key={document.id}
                            className={`knowledge-base-list-item ${selectedDocumentId === document.id ? "selected" : ""}`}
                            onClick={() => setSelectedDocumentId(document.id)}
                        >
                            <strong>{document.title || "Untitled item"}</strong>
                            <span>{document.sourceType} · {document.status}</span>
                            <span>{document.language.toUpperCase()} · {Number(document.chunksCount || 0)} chunks</span>
                        </button>
                    )) : (
                        <div className="knowledge-base-empty-list">
                            <Empty description="No knowledge items yet" />
                        </div>
                    )}
                </div>
            </div>
            <div className="knowledge-base-editor">
                {editorActions}
                <FieldContainer>
                    <div className="knowledge-base-grid">
                        <div>
                            <label>Title</label>
                            <Input
                                size="large"
                                value={form.title}
                                onChange={(e) => updateSelectedDocument({ title: e.target.value })}
                                placeholder="Shipping policy"
                            />
                        </div>
                        <div>
                            <label>Source Type</label>
                            <Select
                                size="large"
                                value={form.sourceType}
                                options={SOURCE_OPTIONS}
                                getPopupContainer={(trigger) => trigger.parentElement!}
                                onChange={(value) => updateSelectedDocument({ sourceType: value })}
                            />
                        </div>
                        <div>
                            <label>Status</label>
                            <Select
                                size="large"
                                value={form.status}
                                options={STATUS_OPTIONS}
                                getPopupContainer={(trigger) => trigger.parentElement!}
                                onChange={(value) => updateSelectedDocument({ status: value })}
                            />
                        </div>
                        <div className="knowledge-base-grid-span-2">
                            <label>Source Key</label>
                            <Input
                                size="large"
                                value={form.sourceKey}
                                onChange={(e) => updateSelectedDocument({ sourceKey: e.target.value })}
                                placeholder="shipping_policy"
                            />
                        </div>
                    </div>
                    <div className="knowledge-base-content-field">
                        <label>Content</label>
                        <TextArea
                            value={form.contentText}
                            onChange={(e) => updateSelectedDocument({ contentText: e.target.value })}
                            autoSize={{ minRows: embedded ? 12 : 18, maxRows: embedded ? 18 : 28 }}
                            placeholder="Paste the exact support content the bot is allowed to rely on."
                        />
                        <div className="knowledge-base-field-note">Current chunk count: {chunkCount || 0}</div>
                    </div>
                </FieldContainer>
            </div>
        </div>
    );
}
